// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Oggetti da monitorare"
// Centro assistenza → guida per amministratori (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// ATTENZIONE: le soglie min/max NON stanno sull'oggetto. Stanno sulla voce di
// SCADENZIARIO (valore_min/valore_max di scadenziario, vedi guida #11). Il
// piano delle guide le attribuiva a questa schermata: e' sbagliato, il form
// dell'oggetto ha solo descrizione/collocazione/note/riferimento.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, GestioneOggetti — elenco con ricerca ("Cerca per
//                    descrizione o collocazione..."), filtro a segmenti
//                    Attivi / Obsoleti / Tutti (con conteggi), "Nuovo oggetto".
//   Form ........... "Descrizione *" (segnaposto: "es. Frigorifero cucina,
//                    Affettatrice, Magazzino secco..."),
//                    "Collocazione / mansione / scopo" ("es. Cucina piano
//                    terra"), "Note aggiuntive", "Strumento di riferimento"
//                    ("es. Termometro taratura T-001, Bilancia BX-200...",
//                    sotto: "Opzionale · identificativo dello strumento
//                    associato a questo oggetto").
//   Stato .......... "Rendi obsoleto" / "Riattiva". Rendere obsoleto chiede
//                    DUE conferme: la prima ("non comparirà più nei
//                    monitoraggi. I dati storici restano consultabili."),
//                    la seconda solo se ci sono attività a scadenziario
//                    collegate — CONFERMA le rende obsolete anche loro
//                    (cascade), ANNULLA le lascia attive.
//   Scheda ......... "Scheda oggetto": anagrafica + attività a scadenziario
//                    (frequenza, soglie, chi monitora, "Verifica: X").
//                    Pulsante "Assegna attività" → apre lo Scadenziario con
//                    l'oggetto preselezionato. Vuota: "Assegna la prima
//                    attività".
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, Passo,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'g0', label: 'Cosa è un «oggetto»' },
  { id: 'g1', label: '1. Creare un oggetto' },
  { id: 'g2', label: '2. La scheda e «Assegna attività»' },
  { id: 'g3', label: '3. Dove stanno le soglie' },
  { id: 'g4', label: '4. Rendere obsoleto' },
  { id: 'g5', label: 'Le trappole' },
  { id: 'g6', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const CampoFinto = ({ etichetta, valore, segnaposto }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{
      fontSize: 10.5, fontWeight: 700, color: LT.n600,
      textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4,
    }}>{etichetta}</div>
    <div style={{
      border: `1px solid ${LT.n300}`, borderRadius: LT.r.md, padding: '8px 10px',
      background: LT.n0, fontSize: 13, color: valore ? LT.n900 : LT.n400,
    }}>{valore || segnaposto || '—'}</div>
  </div>
);

export default function Oggetti() {
  return (
    <GuidaLayout
      titolo="Oggetti da monitorare"
      sottotitolo="Le celle, le attrezzature e gli ambienti su cui si fanno i controlli: come crearli bene, la scheda che raccoglie tutto, e la strada giusta quando uno esce di scena."
      durata="~ 9 minuti"
      destinatario="Per amministratori"
      sezioni={SEZIONI}
    >

      {/* g0 */}
      <H id="g0">Cosa è un «oggetto»</H>

      <P>
        Oggetto è tutto ciò <Strong>su cui</Strong> si fa un controllo periodico: un
        frigorifero, una cella, un'affettatrice; un locale — il magazzino secco, lo
        spogliatoio — o un piano di lavoro; e anche <Strong>una persona o un gruppo di
        persone</Strong>, se volete tenere a scadenziario la loro formazione, gli attestati
        o le visite mediche. Non a caso il campo si chiama «collocazione / mansione /
        scopo». Se ha bisogno di controlli con una cadenza, in LabThree è un oggetto; il{' '}
        <em>quali</em> controlli e <em>ogni quanto</em> lo dirà poi lo scadenziario.
      </P>
      <P>
        La regola d'oro è la granularità: <Strong>un oggetto per ogni cosa che ha una
        temperatura, una pulizia o una manutenzione propria</Strong>. Tre celle non sono
        «le celle»: sono tre oggetti, perché il giorno della non conformità serve sapere{' '}
        <em>quale</em> cella.
      </P>

      {/* g1 */}
      <H id="g1" cat="manutenzioni">1. Creare un oggetto</H>

      <MockFrame
        url="app.labthree.it — Oggetti · Nuovo"
        markers={[
          { n: 1, top: '34%', left: '81%', titolo: 'Descrizione e collocazione',
            testo: 'La descrizione è il nome che comparirà ovunque — schermate, storico, stampe. La collocazione dice dove sta (o a che scopo serve), e diventa il filtro «Area / reparto» dello Storico e delle verifiche.' },
          { n: 2, top: '73.6%', left: '81%', titolo: 'Strumento di riferimento',
            testo: 'Opzionale: l’identificativo dello strumento con cui si misura su questo oggetto — il termometro tarato, la bilancia. Utile quando l’ispettore chiede «con cosa misurate?».' },
        ]}
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.lg, padding: 16, maxWidth: 460, margin: '0 auto',
          }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: LT.n900, marginBottom: 12 }}>
              Nuovo oggetto
            </div>
            <CampoFinto etichetta="Descrizione *" valore="Cella 1 — sale" />
            <CampoFinto etichetta="Collocazione / mansione / scopo" valore="Reparto salatura" />
            <CampoFinto etichetta="Note aggiuntive" segnaposto="Note opzionali..." />
            <CampoFinto etichetta="Strumento di riferimento" valore="Termometro taratura T-001" />
            <div style={{
              marginTop: 12, padding: '10px 14px', borderRadius: LT.r.md,
              background: LT.verde, color: '#fff', textAlign: 'center',
              fontSize: 13, fontWeight: 700,
            }}>Salva oggetto</div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Obbligatoria è solo la <Strong>Descrizione</Strong>, ma la{' '}
        <Strong>Collocazione</Strong> conviene metterla sempre e scriverla{' '}
        <Strong>sempre uguale</Strong>: «Reparto salatura» e «reparto Salatura» per il
        sistema sono due aree diverse, e le tendine di filtro si riempiono di doppioni.
        Sceglietevi i nomi delle aree una volta, e riusateli tali e quali.
      </P>

      {/* g2 */}
      <H id="g2" cat="manutenzioni">2. La scheda e «Assegna attività»</H>

      <P>
        Toccando un oggetto in elenco si apre la <Strong>Scheda oggetto</Strong>:
        l'anagrafica sopra e, sotto, <Strong>tutte le attività che lo scadenziario gli ha
        cucito addosso</Strong> — ogni quanto, con quali soglie, chi monitora e chi
        verifica. È la risposta in una schermata alla domanda «questa cella com'è
        coperta?».
      </P>

      <Passo n="1" titolo="La scorciatoia">
        <P style={{ margin: '4px 0' }}>
          Il pulsante <Tasto>Assegna attività</Tasto> salta direttamente allo
          Scadenziario con l'oggetto già selezionato: create la cella e nel gesto dopo le
          state già assegnando il controllo temperatura. Su un oggetto ancora scoperto il
          pulsante dice <Tasto>Assegna la prima attività</Tasto> — e finché è scoperto,
          per il sistema è come se non esistesse: nessuno lo vedrà mai in lista attività.
        </P>
      </Passo>

      <Passo n="2" titolo="Il controllo incrociato">
        <P style={{ margin: '4px 0' }}>
          Ogni tanto vale la pena scorrere le schede: un oggetto senza attività è un buco
          nel piano di autocontrollo, e qui si vede a colpo d'occhio.
        </P>
      </Passo>

      {/* g3 */}
      <H id="g3" cat="temperature">3. Dove stanno le soglie</H>

      <P>
        Le soglie — il min e il max della temperatura — <Strong>non si scrivono
        sull'oggetto</Strong>: si scrivono sulla voce di scadenziario che lo controlla. Non
        è una stranezza, è il disegno giusto: la stessa cella può avere un controllo
        temperatura con soglie 0–4 °C <em>e</em> una sanificazione settimanale senza
        nessuna soglia. La soglia appartiene al controllo, non alla cella.
      </P>

      <Callout kind="info" titolo="Quindi, se cercate dove impostare min e max">
        La strada è: <Code>Scadenziario</Code> → la voce dell'attività → i campi valore
        minimo e massimo. Oppure, da qui: scheda oggetto → <Tasto>Assegna attività</Tasto>.
        Il come è nella guida sullo Scadenziario.
      </Callout>

      {/* g4 */}
      <H id="g4" cat="pulizie">4. Rendere obsoleto</H>

      <P>
        Quando una cella viene dismessa o un'attrezzatura sostituita, l'oggetto non si
        cancella: si apre la sua scheda e si preme <Tasto>Rendi obsoleto</Tasto>. Sparisce
        dai monitoraggi di ogni giorno, ma <Strong>i dati storici restano
        consultabili</Strong> — le sue registrazioni degli anni passati non si toccano,
        come per le persone disattivate.
      </P>

      <Passo n="1" titolo="La doppia domanda">
        <P style={{ margin: '4px 0' }}>
          Il programma chiede conferma e poi, se l'oggetto ha attività a scadenziario
          collegate, fa una seconda domanda: renderle obsolete anche loro?{' '}
          <Strong>Conferma</Strong> chiude tutto in un colpo; <Strong>Annulla</Strong>{' '}
          rende obsoleto solo l'oggetto e lascia le attività attive — utile solo se
          state per riagganciarle altrove.
        </P>
      </Passo>

      <Passo n="2" titolo="Il ripensamento">
        <P style={{ margin: '4px 0' }}>
          Nell'elenco, il filtro <Tasto>Obsoleti</Tasto> mostra ciò che è uscito di
          scena; dalla scheda, <Tasto>Riattiva</Tasto> lo riporta in servizio. Le attività
          rese obsolete col passaggio precedente, però, vanno riaccese una per una dallo
          Scadenziario.
        </P>
      </Passo>

      {/* g5 */}
      <H id="g5">Le trappole</H>

      <Callout kind="warn" titolo="Obsoleto sì, ma le attività no">
        Se alla seconda domanda rispondete Annulla, le attività dell'oggetto restano
        attive e continuano ad andare in scadenza: gli operatori si trovano in lista il
        controllo di una cella che non esiste più. Se l'oggetto esce di scena davvero, la
        risposta giusta alla seconda domanda è quasi sempre Conferma.
      </Callout>

      <Callout kind="warn" titolo="L'oggetto-fotocopia">
        «Frigo cucina» creato due volte, magari con una maiuscola di differenza, e lo
        scadenziario che assegna i controlli un po' all'uno e un po' all'altro: lo storico
        si spezza in due. Prima di creare, una ricerca nell'elenco — e se il doppione è
        già nato, si sposta tutto su uno e si rende obsoleto l'altro.
      </Callout>

      <Callout kind="warn" titolo="Il nome che non dice niente">
        «Frigo 1» va bene finché i frigoriferi sono due. La descrizione buona è quella che
        un ispettore — o un collega nuovo — capisce senza chiedere: cosa è, e la
        collocazione dice dove sta. Il segnaposto del campo dà la misura: «Frigorifero
        cucina», «Magazzino secco».
      </Callout>

      {/* g6 */}
      <H id="g6">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Posso cancellare un oggetto creato per sbaglio?">
          No, e il motivo è lo storico: se l'oggetto ha anche una sola registrazione, il
          registro deve poterla mostrare per sempre. La strada è renderlo obsoleto. Se è
          nato per sbaglio e non ha mai avuto registrazioni, resta un obsoleto vuoto in
          fondo all'elenco — innocuo.
        </FAQ>

        <FAQ q="Che differenza c'è fra descrizione e collocazione?">
          La descrizione è <em>cosa</em> è l'oggetto («Cella 1 — sale»), la collocazione è{' '}
          <em>dove sta o a che serve</em> («Reparto salatura»). La seconda alimenta il
          filtro Area / reparto dello Storico e della verifica a blocchi: è lei che vi fa
          verificare «una zona alla volta».
        </FAQ>

        <FAQ q="A cosa serve lo «Strumento di riferimento»?">
          A rispondere alla domanda «con cosa misurate?» prima che venga fatta: è
          l'identificativo del termometro o della bilancia usati su quell'oggetto, quello
          che compare sui rapporti di taratura. Facoltativo, ma su celle e bilance vale i
          dieci secondi che costa.
        </FAQ>

        <FAQ q="Come imposto la formazione del personale con gli oggetti?">
          «Anna Bianchi» o «Addetti produzione» come oggetto, la mansione nel campo
          collocazione, e poi a scadenziario le attività: rinnovo attestato ogni 36 mesi,
          sorveglianza sanitaria annuale. Da lì in poi scadenze, registrazioni e storico
          funzionano come per qualsiasi altro controllo. Per le aziende con molte persone
          è allo studio un modulo di formazione dedicato, con piano e curriculum.
        </FAQ>

        <FAQ q="Ho sostituito il frigorifero con uno nuovo: riuso l'oggetto?">
          Meglio di no: rendete obsoleto il vecchio e createne uno nuovo. Lo storico del
          vecchio resta suo — temperature comprese — e il nuovo parte pulito. Riusare la
          scheda mescolerebbe le storie di due macchine diverse.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.bluSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.arrowRight} size={20} stroke={LT.blu} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.blu, fontSize: 14, marginBottom: 4 }}>
            La guida dopo questa
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            <Strong>Lo Scadenziario</Strong>: il motore di tutto — dove gli oggetti appena
            creati ricevono i loro controlli, le soglie e le persone.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
